import classNames from "classnames";
import React, { useEffect } from "react";
import { SubmitErrorHandler, useForm } from "react-hook-form";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { CreateOrUpdateVendorContractRequest, vendorsService } from "@/api";
import { Button } from "@/components/Elements";
import LoadingWrapper from "@/components/loading-wrapper";
import { useMainStore } from "@/contexts/Store";
import { useLoadableData } from "@/hooks/useLoadableData";

import ContractsDetailFieldRenderer from "../components/ContractsDetailFieldRenderer";
import { contractsFields, initialContractsValues } from "../constants";
import { ContractsFormValues } from "../types";

function VendorContractsDetailView() {
  // Import MobX stores
  const mainStore = useMainStore();

  // Hooks
  const history = useHistory();
  const location = useLocation();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState,
    setError,
    clearErrors,
  } = useForm<ContractsFormValues>({
    defaultValues: initialContractsValues,
  });
  const { vendor_id, contract_id } = useParams<{
    vendor_id: string;
    contract_id: string;
  }>();

  // Variables
  const { workspaceID } = mainStore.context;
  const vendorID = Number(vendor_id);
  const contractID = Number(contract_id);
  const hasCreateMatch = location.pathname.endsWith(
    "/contracts/add-new-contracts",
  );
  const startDate = watch("start_date");
  const reviewDate = watch("review_date");
  const endDate = watch("end_date");
  const fields = contractsFields.filter(
    (field) => field.field_name !== "vendor_name",
  );

  // Hooks
  const vendorContract = useLoadableData({
    request: () => vendorsService.getVendorContract(vendorID, contractID),
    onError: () => "Error. Please refresh to try again.",
    enabled: !hasCreateMatch,
  });

  // Variables
  const hasInactiveVendor = !vendorContract.data?.contract?.vendor_is_active;

  // Effects
  useEffect(() => {
    if (!hasCreateMatch) {
      reset({
        renewal: vendorContract.data?.contract?.renewal,
        file: vendorContract.data?.contract?.file,
        start_date: vendorContract.data?.contract?.start_date,
        end_date: vendorContract.data?.contract?.end_date,
        review_date: vendorContract.data?.contract?.review_date,
        notes: vendorContract.data?.contract?.notes,
        terms: vendorContract.data?.contract?.terms,
      });
    }
  }, [vendorContract.data, hasCreateMatch]);

  useEffect(() => {
    if (startDate) {
      clearErrors("start_date");
    }
    if (reviewDate) {
      clearErrors("review_date");
    }
    if (endDate) {
      clearErrors("end_date");
    }
  }, [startDate, reviewDate, endDate]);

  // Functions
  const onInvalid: SubmitErrorHandler<ContractsFormValues> = () => {
    mainStore.toast.setErrorText(
      "There is something not right! Please check if all necessary fields have been filled properly!",
    );
  };

  function handleCreate(data: CreateOrUpdateVendorContractRequest) {
    return vendorsService.createVendorContract(vendorID, data);
  }

  function handleUpdate(data: CreateOrUpdateVendorContractRequest) {
    return vendorsService.updateVendorContract(vendorID, contractID, data);
  }

  const onSubmit = async (data: ContractsFormValues) => {
    if (startDate && reviewDate && startDate > reviewDate) {
      setError("start_date", {});
      setError("review_date", {});
      mainStore.toast.setErrorText(
        "The start date cannot be after the review date!",
      );

      return;
    }

    if (startDate && endDate && startDate >= endDate) {
      setError("start_date", {});
      setError("end_date", {});
      mainStore.toast.setErrorText(
        "The end date must be after the start date!",
      );

      return;
    }

    const serializeData = {
      contract: {
        ...data,
        start_date: data.start_date!,
        end_date: data.end_date!,
        review_date: data.review_date!,
        file: typeof data.file === "string" ? data.file : data.file.id,
      },
    };

    try {
      await (hasCreateMatch
        ? handleCreate(serializeData)
        : handleUpdate(serializeData));
      await vendorContract.refetch();

      if (hasCreateMatch) {
        history.push(
          `/workspaces/${workspaceID}/modules/vendor-due-diligence/${vendorID}/contracts`,
        );
      }

      mainStore.toast.setInfoText(
        `Contract was successfully ${hasCreateMatch ? "created" : "updated"}.`,
      );
    } catch (error) {
      mainStore.toast.setErrorText(
        `An error occurred. Could not ${
          hasCreateMatch ? "create" : "update"
        } contract`,
      );
      window.console.log(error);
    }
  };

  return (
    <LoadingWrapper
      loading={!vendorContract.data && vendorContract.loading}
      errorText={vendorContract.errorText}
      loadingLayout="small-table"
    >
      <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <div
          className={classNames("detail-view", "template", {
            disabled: hasInactiveVendor && !hasCreateMatch,
          })}
        >
          <div className="section-header">
            <div className="title">Contract Info</div>
          </div>
          <div className="section-row two-cols">
            {fields.map((field) => {
              return (
                <div
                  key={field.field_name}
                  className={`column column-${field.data_type}`}
                >
                  <h4>{field.display_name}</h4>
                  <ContractsDetailFieldRenderer
                    control={control}
                    fieldData={field}
                    locked={hasInactiveVendor && !hasCreateMatch}
                  />
                </div>
              );
            })}
          </div>
          <Button
            className="detail-view__button-save"
            label={hasCreateMatch ? "Create Contract" : "Update Contract"}
            disabled={!formState.isDirty}
          />
        </div>
      </form>
    </LoadingWrapper>
  );
}

export default VendorContractsDetailView;
